import { useCallback, useRef } from 'react';

import useSocket from '@/hooks/useSocket';
import { createComponentState } from '@/state';
import { createNotification, getResultFromState, stringify } from '@/utils';
import { Toast } from '@/models';
import useChatMethods from './useChatMethods';

export type Message = {
  id?: string;
  content?: string;
  type?: string;
  room_id: string;
  room_display_name?: string;
  color?: string;
  sessionid?: string;
  datetime?: string;
  user_id?: string;
  role_id?: string;
  role_name?: string;
  user_profile_image_url?: string;
  user_provider_id: string;
  session_id?: string;
  username?: string;
  user_display_name?: string;
  recipeint_read_at?: string;
  recipeint_read_by?: string;
  trip_id?: string;
  trip_scheduled?: string;
  flight_number?: number;
  servicer_iata_airline_code?: string;
  loop_name?: string;
  trip_airport?: string;
};

export type Room = {
  id?: string;
  unreadCount: number;
  displayName: string;
  displayImageUrl: string;
  trip?: {
    tripId: string;
    tripScheduled: string;
    tripFlightNumber: string | number;
    servicerIataAirlineCode: string;
    loopName?: string;
    trip_airport?: string;
  };
  roomType?: ChatRoomType;
  latestMessage: Message;
  latestMessagePerRole?: Record<string, Message>;
};

export type ResponseRoom = {
  id: string;
  unread_count: number;
  unreplied_count: number;
  display_name: string;
  display_image_url: string;
  trip_id: string;
  trip_scheduled: string;
  flight_number: string | number;
  servicer_iata_airline_code: string;
  latest_message: Message;
  loop_name?: string;
  trip_airport?: string;
};

export type RoomPayload = {
  session_id: string;
  role_id: string;
  rooms: ResponseRoom[];
  total_unread_messages: number;
  room_id: string;
  format: ChatRoomType;
  config: {
    client_retry_limit: number;
    client_retry_interval_milliseconds: number;
    client_refresh_interval_milliseconds: number;
  };
};
export type RoomRequest = {
  format?: ChatRoomType;
};
export type RoomsState = Record<ChatRoomType, Record<string, Room>>;
export type UseChatRoomsReturn = [
  RoomsState,
  setRooms: (rooms: RoomsState) => void,
  {
    getRooms: ReturnType<typeof useChatMethods>['getRooms'];
    totalUnread: number;
    sendMessage: ReturnType<typeof useChatMethods>['sendMessage'];
    clearUserTyping: (userId: string) => void;
  },
];
export enum ChatRoomType {
  ALL_CHAT = 'ALL_CHAT',
  CURRENT_CHAT = 'CURRENT_CHAT',
  MY_CHAT = 'MY_CHAT',
}

export const useChatState = createComponentState<{
  sessionId: string;
  roomId: string;
  totalUnread: number;
  rooms: RoomsState;
  usersTyping: any;
  messages: Message[];
  history: Message[];
  config: {
    client_retry_limit: number;
    client_retry_interval_milliseconds: number;
    client_refresh_interval_milliseconds: number;
  };
}>({
  sessionId: undefined,
  roomId: undefined,
  totalUnread: 0,
  rooms: {
    ALL_CHAT: null,
    CURRENT_CHAT: {},
    MY_CHAT: null,
  },
  usersTyping: [],
  messages: [],
  history: [],
  config: {
    client_retry_limit: 10,
    client_retry_interval_milliseconds: 5000,
    client_refresh_interval_milliseconds: 5000,
  },
});

// TODO: Maybe find a better place for this?
export const useSelectedRoomState = createComponentState<{ room: Room }>({
  room: undefined,
});

const useChatRooms = (format?: ChatRoomType): UseChatRoomsReturn => {
  const { getRooms } = useChatMethods();
  const lastWarning = useRef(null);
  const [{ totalUnread, rooms, roomId, sessionId }, setState] = useChatState(
    ({ state: { sessionId, roomId, totalUnread, rooms }, setState }) => [{ sessionId, roomId, totalUnread, rooms }, setState]
  );

  const socket = useSocket({
    'chat.setHistory': ({ payload }) => {
      if (payload?.rooms?.[0].id !== roomId) return;
      setState((current) => ({ ...current, history: payload?.rooms?.[0].history || [] }));
    },
    'chat.setMetadata': ({ payload }: { eventType: string; payload: RoomPayload; overrides: Record<string, unknown> }) => {
      setState((current) => ({
        ...current,
        sessionId: payload?.session_id,
        roomId: current?.roomId ?? payload?.room_id,
        totalUnread: payload?.total_unread_messages,
        rooms: payload?.rooms?.reduce(
          (acc, room) => {
            // Ensure that the room type is one of the ChatRoomType enum values
            if (payload?.format && ChatRoomType[payload?.format]) {
              return {
                ...acc,
                [payload?.format]: {
                  ...(acc[payload?.format] || {}),
                  [room?.id]: {
                    unreadCount: current?.rooms?.[payload?.format]?.[room?.id]?.unreadCount ?? room?.unreplied_count,
                    displayName: room?.display_name || room?.id.split('::')[0] || '',
                    displayImageUrl: room?.display_image_url || null,
                    latestMessage: room?.latest_message || null,
                    trip: {
                      tripId: room?.trip_id || null,
                      tripScheduled: room?.trip_scheduled || null,
                      tripFlightNumber: room?.flight_number || null,
                      servicerIataAirlineCode: room?.servicer_iata_airline_code || null,
                      loopName: room?.latest_message?.loop_name || room?.loop_name || null,
                      trip_airport: room?.latest_message?.trip_airport || null,
                    },
                    roomType: payload?.format,
                  },
                },
              };
            }
            return acc;
          },
          {
            ...current.rooms,
            [payload?.format]: {}, // Clear the rooms for the specific format before adding new ones
          } as RoomsState
        ), // Initialize with an empty object, typed as RoomsState
        config: {
          client_retry_limit: payload?.config?.client_retry_limit ?? 10,
          client_retry_interval_milliseconds: payload?.config?.client_retry_interval_milliseconds ?? 5000,
          client_refresh_interval_milliseconds: payload?.config?.client_refresh_interval_milliseconds ?? 5000,
        },
      }));
    },
    'chat.receiveWarn': ({ payload }) => {
      if (stringify.compare(lastWarning?.current, payload?.message)) return;
      lastWarning.current = payload?.message;
      createNotification(payload?.message, Toast.Type.DANGER, 'Chat Error');
    },
    'chat.receiveMessage': ({ payload }) => {
      // If the room doesn't exist , create a new one and add it
      if (!rooms?.[ChatRoomType.ALL_CHAT]?.[payload?.room_id]) {
        addNewRoom(payload, [ChatRoomType.ALL_CHAT]);
      }
      if (!rooms?.[ChatRoomType.CURRENT_CHAT]?.[payload?.room_id]) {
        addNewRoom(payload, [ChatRoomType.CURRENT_CHAT]);
      }
      if (sessionId === payload?.session_id && !rooms?.[ChatRoomType.MY_CHAT]?.[payload?.room_id]) {
        copyRoom(payload?.room_id, ChatRoomType.ALL_CHAT, ChatRoomType.MY_CHAT);
      }
      // Update Message Information associated to the room
      updateMessage(payload);
      // If the message is not from this room, ignore it
      if (payload.room_id !== roomId) {
        return;
      }
      // Dedupe and then append the new message to the room history
      setState((current) => {
        const updatedMessages = [payload, ...(current?.history || [])];
        const uniqueMessages = new Map(updatedMessages.map((message) => [message.id, message]));
        return { ...current, history: Array.from(uniqueMessages.values()) };
      });
      clearUserTyping(payload?.user_id);
      setState((current) => ({ ...current, messages: [...current.messages, payload] }));
    },
    'chat.receiveUserTyping': ({ payload }: { eventType: string; payload; overrides: Record<string, unknown> }) => {
      const parsedPayload = typeof payload === 'string' ? JSON.parse(payload) : payload;
      if (parsedPayload?.room_id !== roomId || parsedPayload?.session_id === sessionId) return;
      setState((current) => {
        const now = Date.now();
        let found = false;
        const result = current.usersTyping.map((user) => {
          if (user.user_id === parsedPayload.user_id && user.room_id === parsedPayload.room_id) {
            found = true;
            return { ...user, timestamp: now };
          }
          return user;
        });
        if (!found) result.push({ ...parsedPayload, timestamp: now });
        return { ...current, usersTyping: result };
      });
    },
  });
  const updateMessage = useCallback(
    (payload: Message) => {
      const isCrew = payload?.role_name?.toLocaleLowerCase() === 'crew'; // Lowercase role name check
      setState((current) => ({
        ...current,
        rooms: Object.keys(current.rooms).reduce((acc, roomType) => {
          const roomsOfType = current.rooms[roomType as ChatRoomType];

          // Check if this roomType contains the room with the given room_id
          if (roomsOfType?.[payload.room_id]) {
            const room = roomsOfType[payload.room_id];
            const unreadCount = room.latestMessage?.id === payload.id ? room.unreadCount : isCrew ? room.unreadCount + 1 : 0;
            return {
              ...acc,
              [roomType]: {
                ...roomsOfType,
                [payload.room_id]: {
                  ...room,
                  unreadCount,
                  latestMessage: payload,
                },
              },
            };
          }
          // If the room doesn't exist in this roomType, just return it as is
          return {
            ...acc,
            [roomType]: roomsOfType,
          };
        }, {} as RoomsState), // Initial value is an empty object
      }));
    },
    [setState]
  );
  const addNewRoom = useCallback(
    (payload: Message, roomTypes: ChatRoomType[]) => {
      setState((current) => ({
        ...current,
        rooms: roomTypes.reduce((acc, roomType) => {
          return {
            ...acc,
            [roomType]: {
              [payload?.room_id]: {
                unreadCount: 1,
                displayName: payload?.room_display_name,
                displayImageUrl: payload?.user_profile_image_url || '',
                latestMessage: payload,
                roomType,
                trip: {
                  tripId: payload?.trip_id || '',
                  tripScheduled: payload?.trip_scheduled || '',
                  tripFlightNumber: payload?.flight_number || '',
                  servicerIataAirlineCode: payload?.servicer_iata_airline_code || '',
                  trip_airport: payload?.trip_airport || '',
                },
              },
              ...current.rooms[roomType],
            },
          };
        }, current.rooms), // Start with the existing rooms as the initial value
      }));
    },
    [setState]
  );
  const copyRoom = (roomId: string, copyFrom: ChatRoomType, copyTo: ChatRoomType) => {
    if (!roomId || !rooms[copyFrom]?.[roomId]) return;
    setState((current) => ({
      ...current,
      rooms: {
        ...current.rooms,
        [copyTo]: {
          [roomId]: { ...current.rooms[copyFrom]?.[roomId], unreadCount: 0 },
          ...current.rooms[copyTo],
        },
      },
    }));
  };
  const sendMessage = useCallback((payload?: Record<string, unknown>): void => socket.emit('chat.sendMessage', payload), [socket]);
  const setRooms = (rooms: RoomsState | ((current: RoomsState) => RoomsState)) =>
    setState((current) => ({ ...current, rooms: getResultFromState(rooms, current.rooms) }));

  const clearUserTyping = useCallback(
    (userId: string) => {
      setState((current) => ({
        ...current,
        usersTyping: current.usersTyping.filter((user) => user.user_id !== userId),
      }));
    },
    [setState]
  );

  return [rooms, setRooms, { getRooms, totalUnread, sendMessage, clearUserTyping }];
};

export default useChatRooms;
